// Coupe du bâtiment : niveaux empilés, zones, nombre d'équipements, gaine technique qui relie les niveaux.
// Représentation de données réelles. Une liste équivalente est toujours proposée à côté (accessibilité, mobile).
import type { Lieu } from './FormulaireEquipement.tsx';

interface Props {
  batiment: Lieu;
  lieux: Lieu[];
  compte: (lieuId: string) => number;   // équipements dans ce lieu et ses descendants
  selection: string | null;
  choisir: (lieuId: string | null) => void;
}

export function CoupeBatiment({ batiment, lieux, compte, selection, choisir }: Props) {
  const etages = lieux.filter((l) => l.parent_id === batiment.id && l.kind === 'etage')
    .sort((a, b) => (b.level ?? 0) - (a.level ?? 0));
  const zonesDirectes = lieux.filter((l) => l.parent_id === batiment.id && l.kind === 'zone');
  const niveaux = etages.length ? etages : [];
  return (
    <figure className="coupe" aria-label={`Coupe du bâtiment ${batiment.name}`}>
      <div className="coupe-toit" aria-hidden />
      <div className="coupe-corps">
        <div className="coupe-gaine" aria-hidden />
        {niveaux.map((e) => {
          const zones = lieux.filter((l) => l.parent_id === e.id && l.kind === 'zone');
          const n = compte(e.id);
          return (
            <div key={e.id} className={`coupe-niveau ${selection === e.id ? 'est-choisi' : ''}`}>
              <button type="button" className="coupe-etiquette" onClick={() => choisir(selection === e.id ? null : e.id)} aria-pressed={selection === e.id}>
                <span className="coupe-nom">{e.name}</span>
                <span className="tres-petit discret chiffre">{n} équip.</span>
              </button>
              <div className="coupe-zones">
                {zones.length === 0 && <span className="tres-petit discret coupe-sans-zone">Aucune zone définie</span>}
                {zones.map((z) => {
                  const nz = compte(z.id);
                  return (
                    <button key={z.id} type="button" className={`coupe-zone ${selection === z.id ? 'est-choisi' : ''}`} aria-pressed={selection === z.id}
                      onClick={() => choisir(selection === z.id ? null : z.id)} title={`${z.name} : ${nz} équipement(s)`}>
                      <span className="coupe-zone-nom">{z.name}</span>
                      <span className="coupe-points" aria-hidden>{Array.from({ length: Math.min(nz, 8) }).map((_, i) => <i key={i} />)}{nz > 8 && <b>+{nz - 8}</b>}</span>
                      <span className="visuellement-cache">{nz} équipement(s)</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {zonesDirectes.length > 0 && (
          <div className="coupe-niveau">
            <span className="coupe-etiquette"><span className="coupe-nom">Sans étage</span></span>
            <div className="coupe-zones">
              {zonesDirectes.map((z) => (
                <button key={z.id} type="button" className={`coupe-zone ${selection === z.id ? 'est-choisi' : ''}`} aria-pressed={selection === z.id} onClick={() => choisir(selection === z.id ? null : z.id)}>
                  <span className="coupe-zone-nom">{z.name}</span><span className="tres-petit discret">{compte(z.id)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {niveaux.length === 0 && zonesDirectes.length === 0 && <p className="petit discret" style={{ padding: 16 }}>Ajoutez des étages et des zones pour voir la coupe.</p>}
      </div>
      <div className="coupe-sol" aria-hidden />
      <figcaption className="tres-petit discret">{batiment.name} — sélectionnez un niveau ou une zone pour filtrer les équipements.</figcaption>
    </figure>
  );
}
